import type { CSSProperties, ReactNode } from "react"

import {
  GALLERY_CANVAS_ASSET_ROLE,
  GALLERY_CANVAS_LAYER_KIND,
  type GalleryCanvasAsset,
  type GalleryCanvasBounds,
  type GalleryCanvasDocument,
  type GalleryCanvasLayer,
  type GalleryCanvasRenderRequest,
} from "./contract"

function compareCodepoints(left: string, right: string): number {
  if (left < right) {
    return -1
  }
  return left > right ? 1 : 0
}

function assetUrl(asset: GalleryCanvasAsset): string {
  return `data:${asset.mediaType};base64,${asset.bytesBase64}`
}

function requireAsset(
  assets: ReadonlyMap<string, GalleryCanvasAsset>,
  id: string
): GalleryCanvasAsset {
  const asset = assets.get(id)
  if (!asset) {
    throw new Error(`gallery canvas renderer is missing verified asset ${id}`)
  }
  return asset
}

function positionStyle(bounds: GalleryCanvasBounds): CSSProperties {
  return {
    position: "absolute",
    left: `${bounds.x * 100}%`,
    top: `${bounds.y * 100}%`,
    width: `${bounds.width * 100}%`,
    height: `${bounds.height * 100}%`,
  }
}

function commonStyle(layer: GalleryCanvasLayer): CSSProperties {
  return {
    ...positionStyle(layer.bounds),
    zIndex: layer.zIndex,
    opacity: layer.opacity,
    visibility: layer.visible ? "visible" : "hidden",
    transform: `rotate(${layer.rotation}deg)`,
    transformOrigin: "center",
    boxSizing: "border-box",
  }
}

function referencedAssetIds(document: GalleryCanvasDocument): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const layer of document.layers) {
    switch (layer.kind) {
      case GALLERY_CANVAS_LAYER_KIND.background:
      case GALLERY_CANVAS_LAYER_KIND.icon:
        ids.add(layer.assetId)
        break
      case GALLERY_CANVAS_LAYER_KIND.product:
        ids.add(layer.assetId)
        if (layer.maskAssetId) {
          ids.add(layer.maskAssetId)
        }
        break
      case GALLERY_CANVAS_LAYER_KIND.text:
        ids.add(layer.style.fontAssetId)
        for (const assetId of layer.style.fontFallbackAssetIds ?? []) {
          ids.add(assetId)
        }
        break
      case GALLERY_CANVAS_LAYER_KIND.shape:
      case GALLERY_CANVAS_LAYER_KIND.panel:
      case GALLERY_CANVAS_LAYER_KIND.group:
        break
    }
  }
  return ids
}

function textValue(
  document: GalleryCanvasDocument,
  layer: Extract<GalleryCanvasLayer, { kind: "text" }>
) {
  return layer.source.kind === "generic" ? layer.source.text : document.copy[layer.source.field]
}

function BackgroundLayer(input: {
  layer: Extract<GalleryCanvasLayer, { kind: "background" }>
  assets: ReadonlyMap<string, GalleryCanvasAsset>
}) {
  const asset = requireAsset(input.assets, input.layer.assetId)
  return (
    <img
      alt=""
      data-gallery-canvas-layer={input.layer.id}
      data-gallery-canvas-layer-kind={input.layer.kind}
      data-gallery-canvas-protected={
        input.layer.collisionPolicy === "obstacle" ? "true" : undefined
      }
      draggable={false}
      src={assetUrl(asset)}
      style={{
        ...commonStyle(input.layer),
        objectFit: input.layer.fit,
        objectPosition: `${input.layer.focalPoint.x * 100}% ${input.layer.focalPoint.y * 100}%`,
      }}
    />
  )
}

function ProductLayer(input: {
  layer: Extract<GalleryCanvasLayer, { kind: "product" }>
  assets: ReadonlyMap<string, GalleryCanvasAsset>
}) {
  const asset = requireAsset(input.assets, input.layer.assetId)
  const mask = input.layer.maskAssetId
    ? assetUrl(requireAsset(input.assets, input.layer.maskAssetId))
    : undefined
  const shadow = input.layer.contactShadow
  const ambient = input.layer.colorMatch
  const cleanupInset = input.layer.edgeCleanup.inset * 100
  return (
    <div
      data-gallery-canvas-collision-policy={input.layer.collisionPolicy}
      data-gallery-canvas-layer={input.layer.id}
      data-gallery-canvas-layer-kind={input.layer.kind}
      data-gallery-canvas-protected="true"
      style={commonStyle(input.layer)}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: "8%",
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${ambient.ambientColor}${Math.round(
            ambient.strength * 255
          )
            .toString(16)
            .padStart(2, "0")}, transparent 70%)`,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: `${(1 - shadow.scaleX) * 50 + shadow.offsetX * 100}%`,
          bottom: `${-shadow.offsetY * 100}%`,
          width: `${shadow.scaleX * 100}%`,
          height: "8%",
          borderRadius: "50%",
          background: shadow.color,
          opacity: shadow.opacity,
          filter: `blur(${shadow.blur * 100}px)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          clipPath: `inset(${cleanupInset}% round ${input.layer.edgeCleanup.feather * 100}%)`,
          maskImage: mask ? `url("${mask}")` : undefined,
          WebkitMaskImage: mask ? `url("${mask}")` : undefined,
          maskSize: "contain",
          WebkitMaskSize: "contain",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
          maskPosition: "center",
          WebkitMaskPosition: "center",
        }}
      >
        <img
          alt=""
          data-gallery-canvas-approved-cutout="true"
          draggable={false}
          src={assetUrl(asset)}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      </div>
    </div>
  )
}

function TextLayer(input: {
  document: GalleryCanvasDocument
  layer: Extract<GalleryCanvasLayer, { kind: "text" }>
}) {
  const value = textValue(input.document, input.layer)
  const style = input.layer.style
  return (
    <div
      data-gallery-canvas-layer={input.layer.id}
      data-gallery-canvas-layer-kind={input.layer.kind}
      data-gallery-canvas-collision-policy={input.layer.collisionPolicy}
      data-gallery-canvas-protected={
        input.layer.collisionPolicy === "obstacle" ? "true" : undefined
      }
      style={{
        ...commonStyle(input.layer),
        display: "flex",
        alignItems: "center",
        color: style.color,
        fontFamily: style.fontFamily,
        fontSize: `${style.fontSize * 100}vh`,
        fontWeight: style.fontWeight,
        lineHeight: style.lineHeight,
        letterSpacing: `${style.letterSpacing}em`,
        textAlign: style.align,
        whiteSpace: style.wrap === "none" ? "nowrap" : "normal",
        overflowWrap: style.wrap === "none" ? "normal" : "break-word",
        textWrap: style.wrap === "balance" ? "balance" : "wrap",
        overflow: "hidden",
      }}
    >
      <span data-gallery-canvas-text-content style={{ width: "100%" }}>
        {value}
      </span>
    </div>
  )
}

function IconLayer(input: {
  layer: Extract<GalleryCanvasLayer, { kind: "icon" }>
  assets: ReadonlyMap<string, GalleryCanvasAsset>
}) {
  const url = assetUrl(requireAsset(input.assets, input.layer.assetId))
  if (input.layer.tint) {
    return (
      <div
        data-gallery-canvas-layer={input.layer.id}
        data-gallery-canvas-layer-kind={input.layer.kind}
        data-gallery-canvas-protected={
          input.layer.collisionPolicy === "obstacle" ? "true" : undefined
        }
        style={{
          ...commonStyle(input.layer),
          background: input.layer.tint,
          mask: `url("${url}") center / contain no-repeat`,
          WebkitMask: `url("${url}") center / contain no-repeat`,
        }}
      />
    )
  }
  return (
    <img
      alt=""
      data-gallery-canvas-layer={input.layer.id}
      data-gallery-canvas-layer-kind={input.layer.kind}
      data-gallery-canvas-protected={
        input.layer.collisionPolicy === "obstacle" ? "true" : undefined
      }
      draggable={false}
      src={url}
      style={{ ...commonStyle(input.layer), objectFit: "contain" }}
    />
  )
}

function ShapeLayer(input: { layer: Extract<GalleryCanvasLayer, { kind: "shape" }> }) {
  const isLine = input.layer.shape === "line"
  return (
    <div
      data-gallery-canvas-layer={input.layer.id}
      data-gallery-canvas-layer-kind={input.layer.kind}
      data-gallery-canvas-protected={
        input.layer.collisionPolicy === "obstacle" ? "true" : undefined
      }
      style={{
        ...commonStyle(input.layer),
        background: input.layer.fill,
        border: input.layer.stroke
          ? `${input.layer.stroke.width * 100}vh solid ${input.layer.stroke.color}`
          : undefined,
        borderRadius: input.layer.shape === "ellipse" ? "50%" : `${input.layer.radius * 100}%`,
        height: isLine ? "1px" : undefined,
      }}
    />
  )
}

function PanelLayer(input: { layer: Extract<GalleryCanvasLayer, { kind: "panel" }> }) {
  return (
    <div
      data-gallery-canvas-layer={input.layer.id}
      data-gallery-canvas-layer-kind={input.layer.kind}
      data-gallery-canvas-protected={
        input.layer.collisionPolicy === "obstacle" ? "true" : undefined
      }
      style={{
        ...commonStyle(input.layer),
        background: input.layer.fill,
        border: input.layer.border
          ? `${input.layer.border.width * 100}vh solid ${input.layer.border.color}`
          : undefined,
        borderRadius: `${input.layer.radius * 100}%`,
        backdropFilter: input.layer.blur > 0 ? `blur(${input.layer.blur * 100}px)` : undefined,
      }}
    />
  )
}

function renderLayer(input: {
  document: GalleryCanvasDocument
  layer: GalleryCanvasLayer
  assets: ReadonlyMap<string, GalleryCanvasAsset>
  layersById: ReadonlyMap<string, GalleryCanvasLayer>
}): ReactNode {
  switch (input.layer.kind) {
    case GALLERY_CANVAS_LAYER_KIND.background:
      return <BackgroundLayer key={input.layer.id} layer={input.layer} assets={input.assets} />
    case GALLERY_CANVAS_LAYER_KIND.product:
      return <ProductLayer key={input.layer.id} layer={input.layer} assets={input.assets} />
    case GALLERY_CANVAS_LAYER_KIND.text:
      return <TextLayer key={input.layer.id} document={input.document} layer={input.layer} />
    case GALLERY_CANVAS_LAYER_KIND.icon:
      return <IconLayer key={input.layer.id} layer={input.layer} assets={input.assets} />
    case GALLERY_CANVAS_LAYER_KIND.shape:
      return <ShapeLayer key={input.layer.id} layer={input.layer} />
    case GALLERY_CANVAS_LAYER_KIND.panel:
      return <PanelLayer key={input.layer.id} layer={input.layer} />
    case GALLERY_CANVAS_LAYER_KIND.group: {
      const children = input.layer.children
        .map((id) => input.layersById.get(id))
        .filter((layer) => layer !== undefined)
        .sort((left, right) => left.zIndex - right.zIndex || compareCodepoints(left.id, right.id))
      return (
        <div
          key={input.layer.id}
          data-gallery-canvas-group-root={input.layer.id}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: input.layer.zIndex,
            opacity: input.layer.opacity,
            visibility: input.layer.visible ? "visible" : "hidden",
            transform: `rotate(${input.layer.rotation}deg)`,
            transformOrigin: `${(input.layer.bounds.x + input.layer.bounds.width / 2) * 100}% ${(input.layer.bounds.y + input.layer.bounds.height / 2) * 100}%`,
          }}
        >
          <div
            aria-hidden="true"
            data-gallery-canvas-collision-policy={input.layer.collisionPolicy}
            data-gallery-canvas-layer={input.layer.id}
            data-gallery-canvas-layer-kind={input.layer.kind}
            data-gallery-canvas-protected={
              input.layer.collisionPolicy === "obstacle" ? "true" : undefined
            }
            style={{ ...positionStyle(input.layer.bounds), pointerEvents: "none" }}
          />
          {children.map((child) => renderLayer({ ...input, layer: child }))}
        </div>
      )
    }
  }
}

export function GalleryCanvasScene({ request }: { request: GalleryCanvasRenderRequest }) {
  const document = request.document
  const assetsById = new Map(request.assets.map((asset) => [asset.id, asset]))
  const layersById = new Map(document.layers.map((layer) => [layer.id, layer]))
  const groupChildren = new Set(
    document.layers.flatMap((layer) =>
      layer.kind === GALLERY_CANVAS_LAYER_KIND.group ? layer.children : []
    )
  )
  const roots = document.layers
    .filter((layer) => !groupChildren.has(layer.id))
    .sort((left, right) => left.zIndex - right.zIndex || compareCodepoints(left.id, right.id))
  const fonts = request.assets.filter((asset) => asset.role === GALLERY_CANVAS_ASSET_ROLE.font)
  const requiredAssetIds = referencedAssetIds(document)
  const requiredAssets = request.assets.filter((asset) => requiredAssetIds.has(asset.id))
  const requiredFonts = fonts.filter((asset) => requiredAssetIds.has(asset.id))
  const requiredImages = requiredAssets.filter(
    (asset) => asset.role !== GALLERY_CANVAS_ASSET_ROLE.font
  )
  const logicalWidth = document.output.width / document.output.backingScale
  const logicalHeight = document.output.height / document.output.backingScale
  const fontCss = requiredFonts
    .map((font) => {
      if (!font.fontFamily || !font.fontWeightRange || !font.unicodeRange) {
        throw new Error(`gallery canvas font ${font.id} is missing pinned metadata`)
      }
      return `@font-face{font-family:${JSON.stringify(font.fontFamily)};src:url("${assetUrl(font)}") format("woff2");font-display:block;font-style:normal;font-weight:${font.fontWeightRange.min} ${font.fontWeightRange.max};unicode-range:${font.unicodeRange};}`
    })
    .join("")

  return (
    <div
      data-gallery-canvas-root
      data-gallery-canvas-version={document.version}
      data-gallery-canvas-safe-x={document.output.safeBounds.x}
      data-gallery-canvas-safe-y={document.output.safeBounds.y}
      data-gallery-canvas-safe-width={document.output.safeBounds.width}
      data-gallery-canvas-safe-height={document.output.safeBounds.height}
      style={{
        position: "relative",
        width: logicalWidth,
        height: logicalHeight,
        overflow: "hidden",
        isolation: "isolate",
        background: "transparent",
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: fontCss }} />
      <div
        aria-hidden="true"
        style={{ position: "absolute", left: -2, top: -2, width: 1, height: 1, opacity: 0 }}
      >
        {requiredImages.map((asset) => (
          <img
            alt=""
            data-gallery-canvas-required-asset={asset.id}
            key={asset.id}
            src={assetUrl(asset)}
            style={{ display: "block", width: 1, height: 1 }}
          />
        ))}
      </div>
      {roots.map((layer) => renderLayer({ document, layer, assets: assetsById, layersById }))}
    </div>
  )
}

export function galleryCanvasOutputMimeType(document: GalleryCanvasDocument): string {
  return document.output.format === "png" ? "image/png" : "image/jpeg"
}
